import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { Sun, Moon, Home, Search, Tv, Film, Menu, X } from 'lucide-react';
import SearchModal from './SearchModal';
import MovieDetailModal from './MovieDetailModal';
import LiquidFooter from './ui/LiquidFooter';
import PlayerModal from './PlayerModal';

const Layout = ({ children }) => {
    const { isDark, toggleTheme } = useTheme();
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Global detail modal state
    const [detailId, setDetailId] = useState(null);
    const [detailType, setDetailType] = useState('movie');
    const [detailSeed, setDetailSeed] = useState(null);
    const [reopenSearchAfterDetail, setReopenSearchAfterDetail] = useState(false);

    // Global player modal state
    const [playerOpen, setPlayerOpen] = useState(false);
    const [playerSource, setPlayerSource] = useState(null);
    const [playerTitle, setPlayerTitle] = useState('');
    const [playerMediaType, setPlayerMediaType] = useState('movie');
    const [playerSeason, setPlayerSeason] = useState(undefined);
    const [playerEpisode, setPlayerEpisode] = useState(undefined);
    const [playerTmdbId, setPlayerTmdbId] = useState(undefined);

    useEffect(() => {
        const handler = (e) => {
            const { source, title = 'Video Player', mediaType = 'movie', season, episode, tmdbId } = e.detail || {};
            if (!source && !tmdbId) return;
            setPlayerSource(source || null);
            setPlayerTitle(title);
            setPlayerMediaType(mediaType);
            setPlayerSeason(season);
            setPlayerEpisode(episode);
            setPlayerTmdbId(tmdbId);
            setPlayerOpen(true);
        };
        window.addEventListener('open-player', handler);
        return () => window.removeEventListener('open-player', handler);
    }, []);

    const openDetail = (item, sourceQuery) => {
        setDetailId(item.id);
        const type = item.media_type ? item.media_type : (item.title || item.original_title ? 'movie' : 'tv');
        setDetailType(type);
        setDetailSeed(item);
        if (typeof sourceQuery === 'string') {
            setSearchQuery(sourceQuery);
            setReopenSearchAfterDetail(true);
            setIsSearchOpen(false);
        }
    };
    const closeDetail = () => {
        setDetailId(null);
        setDetailSeed(null);
        if (reopenSearchAfterDetail) {
            setIsSearchOpen(true);
            setReopenSearchAfterDetail(false);
        }
    };

    const navItems = [
        { path: '/', label: 'Home', icon: Home },
        { path: '/search', label: 'Search', icon: Search, isSearch: true },
        { path: '/anime', label: 'Anime', icon: Film },
        { path: '/tv-series', label: 'TV-Series', icon: Tv },
        { path: '/movie', label: 'Movie', icon: Film },
    ];

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const handleNavClick = (item) => {
        if (item.isSearch) {
            setIsSearchOpen(true);
            return false;
        }
        return true;
    };

    return (
        <div className="min-h-screen bg-slate-900 ">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 rounded-none dark:rounded-none border-none z-50 glass-light dark:glass-dark">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        {/* Logo */}
                        <Link to="/" className="flex items-center space-x-2">
                            <motion.div
                                whileHover={{ scale: 1.1 }}
                                className="text-2xl font-bold gradient-text"
                            >
                                MovieHub
                            </motion.div>
                        </Link>

                        {/* Desktop Navigation */}
                        <div className="hidden md:flex items-center space-x-8">
                            {navItems.map((item) => {
                                const Icon = item.icon;
                                const isActive = location.pathname === item.path;
                                const onClick = () => handleNavClick(item);
                                return item.isSearch ? (
                                    <button
                                        key={item.path}
                                        onClick={onClick}
                                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-300 ${isActive
                                                ? 'bg-white/20 text-white'
                                                : 'text-white/70 hover:text-white hover:bg-white/10'
                                            }`}
                                    >
                                        <Icon size={18} />
                                        <span>{item.label}</span>
                                    </button>
                                ) : (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-300 ${isActive
                                                ? 'bg-white/20 text-white'
                                                : 'text-white/70 hover:text-white hover:bg-white/10'
                                            }`}
                                    >
                                        <Icon size={18} />
                                        <span>{item.label}</span>
                                    </Link>
                                );
                            })}
                        </div>

                        {/* Theme Toggle & Mobile Menu Button */}
                        <div className="flex items-center space-x-4">
                            <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={toggleTheme}
                                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors duration-300"
                                aria-label="Toggle theme"
                            >
                                {isDark ? <Sun size={20} /> : <Moon size={20} />}
                            </motion.button>

                            {/* Mobile menu button */}
                            <button
                                onClick={toggleMobileMenu}
                                className="md:hidden p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors duration-300"
                                aria-label="Toggle mobile menu"
                            >
                                {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Navigation */}
                <motion.div
                    initial={false}
                    animate={isMobileMenuOpen ? 'open' : 'closed'}
                    variants={{
                        open: { opacity: 1, height: 'auto' },
                        closed: { opacity: 0, height: 0 },
                    }}
                    transition={{ duration: 0.3 }}
                    className="md:hidden overflow-hidden glass-light dark:glass-dark border-t border-white/20"
                >
                    <div className="px-4 py-4 space-y-2">
                        {navItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname === item.path;
                            return item.isSearch ? (
                                <button
                                    key={item.path}
                                    onClick={() => { setIsSearchOpen(true); setIsMobileMenuOpen(false); }}
                                    className={`flex items-center space-x-3 px-3 py-3 rounded-lg transition-all duration-300 ${isActive
                                            ? 'bg-white/20 text-white'
                                            : 'text-white/70 hover:text-white hover:bg-white/10'
                                        }`}
                                >
                                    <Icon size={20} />
                                    <span>{item.label}</span>
                                </button>
                            ) : (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className={`flex items-center space-x-3 px-3 py-3 rounded-lg transition-all duration-300 ${isActive
                                            ? 'bg-white/20 text-white'
                                            : 'text-white/70 hover:text-white hover:bg-white/10'
                                        }`}
                                >
                                    <Icon size={20} />
                                    <span>{item.label}</span>
                                </Link>
                            );
                        })}
                    </div>
                </motion.div>
            </nav>

            {/* Main Content */}
            <main className="pt-16 ">
                {children}
            </main>

            <LiquidFooter />

            {/* Search Modal */}
            <SearchModal
                isOpen={isSearchOpen}
                onClose={() => setIsSearchOpen(false)}
                openDetail={(item, q) => openDetail(item, q)}
                controlledQuery={searchQuery}
                onQueryChange={setSearchQuery}
            />

            {/* Global Movie Detail Modal */}
            {detailId && (
                <MovieDetailModal movieId={detailId} mediaType={detailType} seedItem={detailSeed} onClose={closeDetail} />)
            }

            {/* Global Player Modal */}
            {playerOpen && (
                <PlayerModal
                    tmdbId={playerTmdbId}
                    mediaType={playerMediaType}
                    title={playerTitle}
                    initialSeason={playerSeason}
                    initialEpisode={playerEpisode}
                    fallbackSource={playerSource}
                    onClose={() => setPlayerOpen(false)}
                />
            )}
        </div>
    );
};

export default Layout;
